import { startTransition } from 'react';

interface PendingPublication {
  readonly values: Map<number, unknown>;
  scheduled: boolean;
  readonly flush: () => void;
}

type FunctionalStateSetter<T> = (
  update: (previous: Record<number, T | undefined>) => Record<number, T | undefined>,
) => void;

const pendingBySetter: WeakMap<object, PendingPublication> = new WeakMap<
  object,
  PendingPublication
>();

/**
 * Publish sibling chart leaves in one parent update. SSR-hydrated leaves run
 * their effects in adjacent microtasks; collecting through the next animation
 * frame prevents an outcome-count-dependent sequence of full chart-parent
 * commits. History publication is non-urgent so React may yield it to the
 * chart engine's display-synchronised animation.
 */
export function publishChartLeafData<T>(
  setData: FunctionalStateSetter<T>,
  outcomeIndex: number,
  value: T | undefined,
): void {
  const key = setData as object;
  let pending = pendingBySetter.get(key);

  if (pending === undefined) {
    const values = new Map<number, unknown>();
    pending = {
      values,
      scheduled: false,
      flush: (): void => {
        pendingBySetter.delete(key);
        setData((previous) => {
          let changed = false;
          const next = { ...previous };
          for (const [index, nextValue] of values) {
            if (Object.is(previous[index], nextValue)) continue;
            next[index] = nextValue as T | undefined;
            changed = true;
          }
          return changed ? next : previous;
        });
      },
    };
    pendingBySetter.set(key, pending);
  }

  pending.values.set(outcomeIndex, value);
  if (pending.scheduled) return;
  pending.scheduled = true;
  const scheduled = pending;
  requestAnimationFrame(() => {
    scheduled.scheduled = false;
    startTransition(scheduled.flush);
  });
}
